import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ILanguageContext from './Interfaces/ILanguageContext';
import '../../../lib/i18n';

const LanguageContext = React.createContext<ILanguageContext>(
    {} as ILanguageContext,
);

function LanguageProvider({ children }: Readonly<PropsWithChildren>) {
    const [currentLanguage, setCurrentLanguage] = useState<string>('en-US');
    const {
        i18n: { changeLanguage },
    } = useTranslation();

    const setLanguage = useCallback(
        (language: string) => {
            localStorage.setItem('UserLanguageSelected', language);

            changeLanguage(language);
            setCurrentLanguage(language);
        },
        [changeLanguage],
    );

    const languageToggle = useMemo(
        () => ({ language: currentLanguage, setLanguage }),
        [currentLanguage, setLanguage],
    );

    useEffect(() => {
        const language = localStorage.getItem('UserLanguageSelected');
        const userLangNavigator = navigator.language;

        changeLanguage(language ?? userLangNavigator);
        setCurrentLanguage(language ?? userLangNavigator);
    }, [changeLanguage]);

    return (
        <LanguageContext.Provider value={languageToggle}>
            {children}
        </LanguageContext.Provider>
    );
}

const useLanguage = (): ILanguageContext => {
    return useContext(LanguageContext);
};

export default (() => {
    return {
        LanguageContext,
        LanguageProvider,
        useLanguage,
    };
})();
